import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  addKycRewardConfig,
  getKycRewardConfigurations,
} from "../../../services/kycRewardService";
import Swal from "sweetalert2";

function AddEditKycRewardConfiguration() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const { register, handleSubmit } = useForm();

  const fireToast = (title, message, icon, callback) => {
    Swal.fire({
      title: title,
      text: message,
      icon: icon,
      confirmButtonText: "Ok",
    }).then((result) => {
      if (result.isConfirmed && callback) {
        callback();
      }
    });
  };

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const rewardData = await getKycRewardConfigurations();
        const { success, error, data } = rewardData || {};
        if (success) {
          const formattedData = data.map((item) => ({
            ...item,
            expiry_date: new Date(item.expiry_date).toISOString().split("T")[0],
          }));
          setRows(formattedData);
        } else {
          fireToast("Oops", error.message || "Something went wrong", "error");
        }
      } catch (error) {
        fireToast("Oops", error.message || "Something went wrong", "error");
      }
    };
    fetchRewards();
  }, []);

  const onSubmit = async (formData) => {
    const today = new Date().toISOString().split("T")[0];
    const processedData = rows.map((row, index) => {
      const expiryDate = formData[`expiry_date_${index}`] || "";
      if (expiryDate && expiryDate < today) {
        alert(`Expiry date for Age ${row.age} cannot be in the past.`);
      }
      return {
        ...row,
        cashback_amount: Number(formData[`cashback_amount_${index}`]) || 0,
        expiry_date: new Date(expiryDate).getTime(),
      };
    });

    try {
      const response = await addKycRewardConfig({ data: processedData });
      const { success, error } = response || {};
      if (success) {
        fireToast(
          "Success",
          "Reward configurations saved successfully.",
          "success",
          () => {
            navigate("/app/kyc-reward-configuration");
          }
        );
      } else {
        fireToast("Oops", error.message || "Something went wrong", "error");
      }
    } catch (error) {
      fireToast("Oops", error.message || "Something went wrong", "error");
    }
  };

  const addRow = () => {
    if (rows.length < 5) {
      setRows([
        ...rows,
        { age: rows.length + 1, cashback_amount: 0, expiry_date: "" },
      ]);
    }
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows.map((row, i) => ({ ...row, age: i + 1 })));
  };

  return (
    <Box sx={{ px: 6 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "25px",
          marginTop: "20px",
          gap: "16px",
        }}
      >
        <Link to={"/app/kyc-reward-configuration"}>
          <ArrowBackIcon
            fontSize="large"
            l
            color="secondary"
            sx={{ background: "#040721", p: 1, borderRadius: "50%" }}
          />
        </Link>
        <Typography variant="h4" component="h1" gutterBottom>
          KYC Reward Configuration
        </Typography>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="row">
        {rows.map((row, index) => (
          <div key={index} className="col-md-12 mb-4">
            <div className="row">
              <div className="col-md-5">
                <div className="form-group">
                  <label className="form-label fw-bold text-secondary">
                    Age {row.age} Cashback Amount:
                  </label>
                  <input
                    {...register(`cashback_amount_${index}`)}
                    type="number"
                    defaultValue={row.cashback_amount}
                    className="form-control"
                    required
                  />
                </div>
              </div>
              <div className="col-md-5">
                <div className="form-group">
                  <label className="form-label fw-bold text-secondary">
                    Expiry Date:
                  </label>
                  <input
                    {...register(`expiry_date_${index}`)}
                    type="date"
                    defaultValue={row.expiry_date}
                    className="form-control"
                    required
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Button
                  onClick={() => deleteRow(index)}
                  variant="outlined"
                  color="error"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        ))}

        <div className="col-md-12 mt-3">
          <Button
            onClick={addRow}
            variant="contained"
            color="secondary"
            disabled={rows.length >= 5}
            className="me-3"
          >
            Add Another Age
          </Button>
          <Button
            disabled={rows.length === 0}
            type="submit"
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Box>
  );
}

export default AddEditKycRewardConfiguration;
